import { useCallback } from 'react';

import { ORDERBOOK_MAX_TICK_SIZES } from 'config/constants';
import { useSelectMarketTickSize } from 'reduxState/markets/listed/listedMarketsUtils';
import { staticDispatch, useSelector } from 'reduxState/store';

import {
  changeMarketTickSize,
  removeStoredMarketTickSize,
} from './userTickSizeReducer';

export function useCurrentTickSize() {
  const userMarketTickSizeMap = useSelector(
    state => state.markets.userMarketTickSize
  );
  const currentMarket = useSelector(state => state.markets.currentMarket.pair);
  const currentMarketTickSize =
    useSelectMarketTickSize(currentMarket)?.asDecimal;

  const maximumTickSize =
    currentMarket && ORDERBOOK_MAX_TICK_SIZES[currentMarket]
      ? Math.max(1, ORDERBOOK_MAX_TICK_SIZES[currentMarket] || 1)
      : // Fallback: show 3 levels if market not configured. Previously used sdk@useOrderbookMaximumTickSize
        Number(currentMarketTickSize) * 100;

  const userTickSizeUncorrected = currentMarket
    ? userMarketTickSizeMap[currentMarket]
    : undefined;

  // Correct if market minimum tick size was updated,
  // so we dont go over it.
  const selectedTickSize =
    !currentMarketTickSize || !userTickSizeUncorrected
      ? undefined
      : Number(userTickSizeUncorrected) < Number(currentMarketTickSize) ||
        Number(userTickSizeUncorrected) > maximumTickSize
      ? currentMarketTickSize
      : userTickSizeUncorrected;

  const onChange = useCallback(
    (tickSize: string) => {
      if (!currentMarket) {
        return;
      }
      if (currentMarketTickSize === tickSize) {
        staticDispatch(removeStoredMarketTickSize(currentMarket));
      } else {
        staticDispatch(changeMarketTickSize(currentMarket, tickSize));
      }
    },
    [currentMarket, currentMarketTickSize]
  );

  if (!currentMarket || !currentMarketTickSize) {
    return undefined;
  }

  return {
    selected: selectedTickSize || currentMarketTickSize,
    default: currentMarketTickSize,
    maximumTickSize,
    onChange,
  };
}
